
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, ChangeDetectorRef, inject } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { VncLibraryService } from "@uxf/vnc-library";
import { take } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { TasksConstants } from "../../task/shared/task-constacts";
import { TaskService } from "../../task/task.service";
import { MessageTranslatorService } from "../../task/services";
import { ChangeServerDialogComponent } from "../../task/components/change-server-dialog/change-server-dialog.component";
import { StorageService } from "../services/storage.service";
import { Broadcaster } from "./broadcaster.service";
import { ConfigService } from "./config.service";
import { CommonUtil } from "../utils/common.utils";

/**
 * Handles the window "message" traffic the shell listens for: the login/registration
 * iframes and the deep-link handlers both talk to the app this way. Extracted from
 * AppComponent (STR-05).
 *
 * The origin check (XSS-06) stays at the AppComponent HostListener, before anything here
 * runs, and is asserted again in handleMessage so this service cannot be called with an
 * unchecked event from somewhere else later.
 */
@Injectable({
  providedIn: "root"
})
export class WindowMessageService {
  private router = inject(Router);
  private matDialog = inject(MatDialog);
  private taskService = inject(TaskService);
  private broadcaster = inject(Broadcaster);
  private configService = inject(ConfigService);
  private messageTranslatorService = inject(MessageTranslatorService);
  private vncLibaryService = inject(VncLibraryService);

  /**
   * @param isOnline the shell's current online status; several branches fall back to an
   *        offline notice instead of navigating.
   */
  public handleMessage(event: MessageEvent, isOnline: boolean, changeDetectionRef: ChangeDetectorRef): void {
    if (!CommonUtil.isTrustedMessageEvent(event)) {
      console.warn("[WindowMessageService] ignored message from untrusted origin", event.origin);
      return;
    }

    const eventData = event.data;
    if (!eventData) {
      return;
    }

    switch (eventData.type) {
      case TasksConstants.GO_TO_REGISTRATION_PAGE:
        this.whenOnline(isOnline, () => {
          if (this.configService.get("isUserRegistrationViaVNCd")) {
            this.configService.hideForgotPasswordIframe();
            this.configService.registrationIframe();
          } else {
            this.router.navigate(["/registration/form"], { queryParams: {} });
          }
        });
        break;

      case TasksConstants.GO_TO_FORGOT_PASSWORD_PAGE:
        this.whenOnline(isOnline, () => {
          if (this.configService.get("isForgotPasswordViaVNCd")) {
            this.configService.hideRegistrationIframe();
            this.configService.forgotPasswordIframe();
          } else {
            this.router.navigate(["/forgot-password/form"], { queryParams: {} });
          }
        });
        break;

      case TasksConstants.GO_TO_RESET_PASSWORD_PAGE:
        this.whenOnline(isOnline, () => {
          this.router.navigate(["/forgot-password/reset"], { queryParams: {} });
        });
        break;

      case TasksConstants.INTERNET_LOST_CONNECTION:
        this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
        break;

      case TasksConstants.CHANGE_SERVER_URL:
        this.whenOnline(isOnline, () => this.showChangeServerDialog(changeDetectionRef));
        break;

      case TasksConstants.GO_TO_CONFIRM_EMAIL:
        this.confirmEmail();
        break;

      case TasksConstants.OPEN_TASK_DETAIL_PAGE:
        this.openTaskDetail(eventData);
        break;

      case TasksConstants.GO_TO_TASK_INVITATION:
        this.acceptInvitation();
        break;

      case TasksConstants.SHOW_DESCRIPTION_DIFFERENCE:
        this.showDescriptionDifference(eventData);
        break;
    }
  }

  public showChangeServerDialog(changeDetectionRef: ChangeDetectorRef): void {
    this.matDialog.open(ChangeServerDialogComponent, {
      maxHeight: "100%",
      maxWidth: "100%",
      autoFocus: false,
    });
    changeDetectionRef.detectChanges();
  }

  private whenOnline(isOnline: boolean, action: () => void): void {
    if (isOnline) {
      action();
    } else {
      this.broadcaster.broadcast("OFFLINE_CONNECTION", true);
    }
  }

  private confirmEmail(): void {
    const activateToken = StorageService.getLocalItem("activation_token");
    if (!activateToken) {
      return;
    }

    // Cordova reaches this handler once the webview is already up; the browser build
    // needs the longer delay for the router to settle.
    const delay = environment.isCordova ? 200 : 2000;
    setTimeout(() => {
      this.taskService.activateAccount(activateToken).subscribe(res => {
        if (res && res.includes(TasksConstants.EXPIRED)) {
          this.showMessage(TasksConstants.ACTIVATION_LINK_EXPIRED_MESSAGE);
          this.router.navigate(["/registration/expired"], { queryParams: {} });
        } else {
          this.router.navigate(["/task"], { queryParams: {} });
        }
      });
    }, delay);
  }

  private openTaskDetail(eventData): void {
    const token = StorageService.getLocalItem("token");
    if (token) {
      this.router.navigate(["/task/all/detail/" + eventData.id], { queryParams: {} });
    } else {
      StorageService.setLocalItem("open-task-id", eventData.id);
      this.router.navigate(["/task"], { queryParams: {} });
    }
  }

  private acceptInvitation(): void {
    const token = StorageService.getLocalItem("token");
    const invitationToken = StorageService.getLocalItem("invitation_token");
    if (!token || !invitationToken) {
      this.router.navigate(["/task"], { queryParams: {} });
      return;
    }

    this.taskService.acceptInvitation(invitationToken).subscribe(res => {
      StorageService.removeLocalItem("invitation_token");
      if (res && res._body.includes(TasksConstants.EXPIRED)) {
        this.showMessage(TasksConstants.TOKEN_EXPIRED);
      } else {
        this.router.navigate(["/task"], { queryParams: {} });
      }
    });
  }

  private showDescriptionDifference(eventData): void {
    const token = StorageService.getLocalItem("token");
    StorageService.setLocalItem("desc_journal_id", eventData.journal_id);
    StorageService.setLocalItem("desc_detail_id", eventData.detail_id);
    if (token) {
      this.router.navigate(["/task/all/detail/" + eventData.id], { queryParams: { show_desc_diff: true } });
    } else {
      StorageService.setLocalItem("open-task-id", eventData.id);
      this.router.navigate(["/task"], { queryParams: {} });
    }
  }

  private showMessage(key: string): void {
    const message = this.messageTranslatorService.getMessage(key);
    this.vncLibaryService.openSnackBar(message, "", "", "", 3000, "bottom", "left").pipe(take(1)).subscribe();
  }
}
